import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWithAuth } from "../../../auth/api";
import {
  HiArrowLeft,
  HiPhotograph,
  HiVideoCamera,
  HiUpload,
  HiLink,
} from "react-icons/hi";

function EditGallery() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    media_type: "image",
    format_type: "16:9",
    category: "General",
    url_input: "",
    is_active: true,
  });

  const [uploadMode, setUploadMode] = useState("url");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [
    "General",
    "Roof Waterproofing",
    "Basement Waterproofing",
    "Bathroom & Wet Areas",
    "Commercial Projects",
    "Residential Projects",
    "Chemical Application",
    "Inspection & Testing",
  ];

  useEffect(() => {
    fetchItem();
  }, [id]);

  const fetchItem = async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth("GET", `getGalleryById/${id}`);
      const data = response.data;
      if (data) {
        setFormData({
          title: data.title || "",
          description: data.description || "",
          media_type: data.media_type || "image",
          format_type: data.format_type || "16:9",
          category: data.category || "General",
          url_input: data.url || "",
          is_active: data.is_active !== undefined ? data.is_active : true,
        });
        setPreviewUrl(data.url);
      }
    } catch (error) {
      toast.error("Failed to load gallery item details");
      navigate("/admin/dashboard/gallery");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 15 * 1024 * 1024) {
        toast.error("File size exceeds 15MB limit");
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setUploadMode("file");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    setIsSubmitting(true);

    try {
      if (formData.media_type === "image" && uploadMode === "file" && selectedFile) {
        const bodyFormData = new FormData();
        bodyFormData.append("title", formData.title);
        bodyFormData.append("description", formData.description);
        bodyFormData.append("media_type", formData.media_type);
        bodyFormData.append("format_type", formData.format_type);
        bodyFormData.append("category", formData.category);
        bodyFormData.append("is_active", formData.is_active);
        bodyFormData.append("image", selectedFile);

        await fetchWithAuth("PUT", `editGallery/${id}`, bodyFormData, true);
      } else {
        await fetchWithAuth("PUT", `editGallery/${id}`, {
          ...formData,
          url_input: formData.url_input.trim(),
        });
      }

      toast.success("Gallery item updated successfully");
      navigate("/admin/dashboard/gallery");
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to update gallery item";
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-4">
          Loading Details...
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500 pb-12">
      {/* Back Button */}
      <div className="flex items-center justify-between">
        <Link
          to="/admin/dashboard/gallery"
          className="inline-flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors"
        >
          <HiArrowLeft size={16} /> Back to Gallery
        </Link>
      </div>

      <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl overflow-hidden">
        <div className="bg-slate-900 px-8 py-6 text-white flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-black tracking-tight">
              Edit Gallery Item #{id}
            </h1>
            <p className="text-xs text-slate-400 font-medium mt-1">
              Update media link, aspect ratio, category, or title details.
            </p>
          </div>
          <div className="w-12 h-12 bg-blue-600/20 border border-blue-500/30 text-blue-400 rounded-2xl flex items-center justify-center">
            {formData.media_type === "image" ? (
              <HiPhotograph size={24} />
            ) : (
              <HiVideoCamera size={24} />
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          {/* Media Type */}
          <div>
            <label className="block text-xs font-black uppercase tracking-widest text-slate-700 mb-3">
              1. Media Type
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData((p) => ({ ...p, media_type: "image" }))}
                className={`p-4 rounded-2xl border-2 flex items-center justify-center gap-3 font-bold text-sm transition-all ${
                  formData.media_type === "image"
                    ? "border-blue-600 bg-blue-50/50 text-blue-600 shadow-md"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <HiPhotograph size={20} /> Photo
              </button>
              <button
                type="button"
                onClick={() => setFormData((p) => ({ ...p, media_type: "video" }))}
                className={`p-4 rounded-2xl border-2 flex items-center justify-center gap-3 font-bold text-sm transition-all ${
                  formData.media_type === "video"
                    ? "border-purple-600 bg-purple-50/50 text-purple-600 shadow-md"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <HiVideoCamera size={20} /> Video Link
              </button>
            </div>
          </div>

          {/* Aspect Format */}
          <div>
            <label className="block text-xs font-black uppercase tracking-widest text-slate-700 mb-3">
              2. Format Type
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label
                className={`p-4 rounded-2xl border-2 cursor-pointer flex flex-col justify-center gap-1 transition-all ${
                  formData.format_type === "16:9"
                    ? "border-blue-600 bg-blue-50/30 text-slate-900"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="format_type"
                    value="16:9"
                    checked={formData.format_type === "16:9"}
                    onChange={handleChange}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="font-bold text-sm">16:9 Widescreen / Landscape</span>
                </div>
              </label>

              <label
                className={`p-4 rounded-2xl border-2 cursor-pointer flex flex-col justify-center gap-1 transition-all ${
                  formData.format_type === "9:16"
                    ? "border-purple-600 bg-purple-50/30 text-slate-900"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="format_type"
                    value="9:16"
                    checked={formData.format_type === "9:16"}
                    onChange={handleChange}
                    className="text-purple-600 focus:ring-purple-500"
                  />
                  <span className="font-bold text-sm">9:16 Portrait / Shorts / Reels</span>
                </div>
              </label>
            </div>
          </div>

          {/* Media URL or File Input */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-black uppercase tracking-widest text-slate-700">
                3. Media Content
              </label>
              {formData.media_type === "image" && (
                <div className="flex gap-2 bg-slate-100 p-1 rounded-xl">
                  <button
                    type="button"
                    onClick={() => setUploadMode("url")}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                      uploadMode === "url"
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500"
                    }`}
                  >
                    Image URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploadMode("file")}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                      uploadMode === "file"
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500"
                    }`}
                  >
                    Upload New File
                  </button>
                </div>
              )}
            </div>

            {formData.media_type === "image" && uploadMode === "file" ? (
              <div className="border-2 border-dashed border-slate-300 hover:border-blue-500 rounded-2xl p-6 text-center bg-slate-50 relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />
                <div className="flex flex-col items-center gap-2">
                  <HiUpload size={24} className="text-blue-600" />
                  <p className="text-xs font-bold text-slate-700">
                    {selectedFile ? selectedFile.name : "Click to choose a replacement file"}
                  </p>
                </div>
              </div>
            ) : (
              <div className="relative">
                <HiLink
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  size={20}
                />
                <input
                  type="url"
                  name="url_input"
                  value={formData.url_input}
                  onChange={handleChange}
                  placeholder="https://..."
                  className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium focus:outline-none focus:border-blue-500"
                />
              </div>
            )}

            {previewUrl && (
              <div className="mt-3">
                <p className="text-[10px] font-black uppercase text-slate-400 mb-1">Current Preview:</p>
                <div className="max-w-xs rounded-xl overflow-hidden border border-slate-200 h-36 bg-slate-900">
                  {formData.media_type === "image" ? (
                    <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white text-xs font-mono p-4 break-all">
                      {formData.url_input}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Title & Category */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-slate-700 mb-2">
                4. Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium focus:outline-none focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-slate-700 mb-2">
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-800 focus:outline-none focus:border-blue-500"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-black uppercase tracking-widest text-slate-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleChange}
              className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium focus:outline-none focus:border-blue-500"
            ></textarea>
          </div>

          {/* Status */}
          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-200">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="is_active" className="text-sm font-bold text-slate-800 cursor-pointer">
              Active in public Gallery
            </label>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end gap-4 pt-4 border-t border-slate-100">
            <Link
              to="/admin/dashboard/gallery"
              className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-xs sm:text-sm transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs sm:text-sm transition-all shadow-lg shadow-blue-500/20 active:scale-95 disabled:opacity-50"
            >
              {isSubmitting ? "Updating..." : "Update Gallery Item"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditGallery;
